import React from 'react';

interface FollowModeHUDProps {
  planeId: string;
  altitude: number | string;
  heading: number;
  speed: number | null;
  onStopFollow: () => void;
}

const getAltitudeColor = (altitude: number): string => {
  if (altitude < 5000) return '#3CAEA3';
  if (altitude < 15000) return '#F6D55C';
  if (altitude < 25000) return '#ED553B';
  return '#9370DB';
};

const formatAltitude = (alt: number | string): string => {
  if (alt === 'ground' || alt === 0) return 'GND';
  const num = typeof alt === 'number' ? alt : parseFloat(String(alt));
  if (isNaN(num)) return 'GND';
  if (num >= 18000) return `FL${Math.round(num / 100)}`;
  return `${num.toLocaleString()} ft`;
};

const FollowModeHUD: React.FC<FollowModeHUDProps> = ({
  planeId,
  altitude,
  heading,
  speed,
  onStopFollow,
}) => {
  const altNum = altitude === 'ground' ? 0 : typeof altitude === 'number' ? altitude : parseFloat(String(altitude)) || 0;
  const altPct = Math.min(100, (altNum / 45000) * 100);
  const altColor = getAltitudeColor(altNum);

  return (
    <div className="follow-hud">
      {/* Aircraft ID */}
      <div className="hud-id">{planeId}</div>

      <div className="hud-divider" />

      {/* Altitude */}
      <div className="hud-stat">
        <div className="hud-alt-gauge">
          <div
            className="hud-alt-fill"
            style={{ height: `${altPct}%`, backgroundColor: altColor }}
          />
        </div>
        <div className="hud-stat-content">
          <span className="hud-stat-label">ALT</span>
          <span className="hud-stat-value">{formatAltitude(altitude)}</span>
        </div>
      </div>

      <div className="hud-divider" />

      {/* Heading with compass */}
      <div className="hud-stat">
        <svg className="hud-compass" width="28" height="28" viewBox="0 0 28 28">
          <circle cx="14" cy="14" r="12" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
          {/* N/E/S/W ticks */}
          <line x1="14" y1="3" x2="14" y2="5" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
          <line x1="25" y1="14" x2="23" y2="14" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
          <line x1="14" y1="25" x2="14" y2="23" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
          <line x1="3" y1="14" x2="5" y2="14" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
          {/* Needle */}
          <g transform={`rotate(${heading}, 14, 14)`} style={{ transition: 'transform 0.8s ease' }}>
            <polygon points="14,4 11.5,16 14,14.5 16.5,16" fill="#3b82f6" opacity="0.9" />
            <polygon points="14,24 11.5,16 14,17.5 16.5,16" fill="rgba(255,255,255,0.25)" />
          </g>
        </svg>
        <div className="hud-stat-content">
          <span className="hud-stat-label">HDG</span>
          <span className="hud-stat-value">{Math.round(heading)}°</span>
        </div>
      </div>

      <div className="hud-divider" />

      {/* Speed */}
      <div className="hud-stat">
        <div className="hud-stat-content">
          <span className="hud-stat-label">SPD</span>
          <span className="hud-stat-value">
            {speed != null ? `${Math.round(speed)} kts` : '--'}
          </span>
        </div>
      </div>

      {/* Stop following button */}
      <button className="hud-close" onClick={onStopFollow} title="Stop following">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default FollowModeHUD;
